import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Text, Field as RawField, TextInput, tokens } from '@hotjar/hotsauce';
import { LOGIN } from '@hotjar/webapp/constants/routes/auth';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { validateEmail } from '@hotjar/webapp/lib/email';
import { TextHorizontalRule } from '@hotjar/webapp/components/TextHorizontalRule';
import { usePermissionRequiredTracking } from '../hooks/usePermissionRequiredTracking';
import { RequestAccessResult } from '../types';
import { SuccessScreen } from './SuccessScreen';
import { Button } from './Button';
import { Title } from './Title';

const INPUT_ID = 'email';

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const Field = styled(RawField)`
  width: auto;
  margin: ${tokens.spacer.x3} 0 ${tokens.spacer.x4};
`;

interface UnauthenticatedScreenProps {
  requestAccessResult: RequestAccessResult;
  requestAccess: (email: string, cb: (err: string) => void) => void;
  isProcessing: boolean;
}

interface RequestAccessFormData {
  email: string;
}

export function UnauthenticatedScreen({
  requestAccess,
  isProcessing = false,
  requestAccessResult = RequestAccessResult.NOT_SENT,
}: UnauthenticatedScreenProps): React.ReactElement {
  const { t } = useTranslation();
  const { trackClick } = usePermissionRequiredTracking();
  const { register, handleSubmit, errors, setError } = useForm<RequestAccessFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const handleInlineSubmitErrors = (err: string) => {
    setError(INPUT_ID, { message: err });
  };

  const onSubmit = ({ email }: RequestAccessFormData) => {
    requestAccess(email, handleInlineSubmitErrors);
  };

  if (requestAccessResult === RequestAccessResult.SUCCESS) {
    return <SuccessScreen />;
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Title>{t('activation:permission-required.title')}</Title>
      <Text textType="body1" textColor="text_light">
        {t('activation:permission-required.unauthenticated.text')}
      </Text>
      <Field
        inputId={INPUT_ID}
        labelText={t('activation:permission-required.unauthenticated.email.label')}
        invalidText={errors.email?.message}
        isInvalid={!!errors.email}
        isRequired
      >
        <TextInput
          id={INPUT_ID}
          name={INPUT_ID}
          autoFocus
          ref={
            register &&
            register({
              required: t(
                'activation:permission-required.unauthenticated.email.required',
              ) as string,
              validate: (value: string) =>
                validateEmail(value) ||
                (t('activation:permission-required.unauthenticated.email.invalid') as string),
            })
          }
          placeholder={t('activation:permission-required.unauthenticated.email.placeholder')}
          isInvalid={!!errors.email}
          isRequired
        />
      </Field>
      <Button
        kind="primary"
        type="subbmit"
        onClick={() => trackClick('Request Access')}
        isLoading={isProcessing}
      >
        {t('activation:permission-required.request-access')}
      </Button>
      <TextHorizontalRule message={t('activation:permission-required.or')} />
      <Button
        as="a"
        href={`${getRoute(LOGIN.path)}${window.location.search}`}
        onClick={() => trackClick('Sign In')}
        kind="secondary"
      >
        {t('activation:permission-required.unauthenticated.secondary-button')}
      </Button>
    </Form>
  );
}
